import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Skeleton,

} from '@mui/material';
import {
  People,
  Business,
  BookOnline,
  Payment,
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  Class,
  EventNote,
  HourglassBottom,
} from '@mui/icons-material';
import {
  HoverPopover,
  SegmentTransactionsDialog,
  TxnCountText,
  formatINR,
  type TransactionStats,
  type SegmentSelection,
} from './TransactionsPage';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LabelList,
  PieChart,
  Pie,
} from 'recharts';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import StudioAnalytics from '../components/analytics/StudioAnalytics';
import { useAuth } from '../contexts/AuthContext';
import { isOwnerRole } from '../utils/roles';
import { ALL_LOCATIONS, getSelectedLocation } from '../components/LocationSwitcher';
import { deriveRevenue } from '../utils/revenue';
import BrandDashboard from '../components/BrandDashboard';

// ─────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────

const MONTHS = [
  { value: 0, label: 'All Year' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthKey(d: Date) { return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`; }

// Build a {start,end} ISO range for a given year/month (month 0 = whole year).
function revenueRangeISO(year: number, month: number) {
  const start = month === 0 ? new Date(Date.UTC(year, 0, 1)) : new Date(Date.UTC(year, month - 1, 1));
  const end = month === 0
    ? new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999))
    : new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

// IST-boundary {startDate,endDate} for the dashboard "Total Revenue" card
// (super admin and single-branch studio owner) — mirrors TransactionsPage's
// computeMonthRange/toISTStart/toISTEnd exactly (including clamping the end
// to "now" when the period is the current month/year) so this card always
// matches the Transactions tab's own number, for whichever role is viewing.
// month 0 = whole year.
const istDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const getTodayIST = () => istDateFormatter.format(new Date()); // 'YYYY-MM-DD'
const toISTStart = (yyyy: string, mm: string, dd: string) => `${yyyy}-${mm}-${dd}T00:00:00+05:30`;
const toISTEnd = (yyyy: string, mm: string, dd: string) => `${yyyy}-${mm}-${dd}T23:59:59.999+05:30`;

function istRevenueRangeISO(year: number, month: number): { startDate: string; endDate: string } {
  const todayIST = getTodayIST();
  const [todayY, todayM, todayD] = todayIST.split('-');
  const yyyy = String(year);
  const isCurrentYear = yyyy === todayY;

  if (month === 0) {
    const isThisYearSoFar = isCurrentYear;
    return {
      startDate: toISTStart(yyyy, '01', '01'),
      endDate: isThisYearSoFar ? toISTEnd(todayY, todayM, todayD) : toISTEnd(yyyy, '12', '31'),
    };
  }

  const mm = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  const isCurrentMonth = isCurrentYear && mm === todayM;
  const endDay = isCurrentMonth ? todayD : String(lastDay).padStart(2, '0');
  return {
    startDate: toISTStart(yyyy, mm, '01'),
    endDate: toISTEnd(yyyy, mm, endDay),
  };
}


// Returns true only for users that registered via Sweat Social (not a whitelabelled app).
function isSweatSocialUser(u: any): boolean {
  const src = u.registrationSource;
  if (typeof src === 'string' && src.startsWith('removed_by_')) return false;
  return !src || src === 'mobile_app' || src === 'website' || src === 'admin';
}

const statusColor = (status: string) => {
  if (!status) return 'default';
  const s = status.toLowerCase();
  if (s === 'confirmed' || s === 'paid') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'cancelled' || s === 'canceled') return 'error';
  return 'default';
};

function buildMonthKeys() {
  const keys: string[] = [];
  const start = new Date(2025, 3, 1); // April 2025
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const d = new Date(start);
  while (d <= end) {
    keys.push(monthKey(new Date(d)));
    d.setMonth(d.getMonth() + 1);
  }
  return keys;
}

function calcMonthlyBookings(bookings: any[]) {
  const keys = buildMonthKeys();
  const data: { [k: string]: number } = {};
  keys.forEach(k => (data[k] = 0));
  bookings.forEach((b: any) => {
    const k = monthKey(new Date(b.createdAt));
    if (k in data) data[k]++;
  });
  return keys.map(k => ({ month: k, bookings: data[k] }));
}

function calcMonthlyUsers(users: any[]) {
  const keys = buildMonthKeys();
  const data: { [k: string]: number } = {};
  keys.forEach(k => (data[k] = 0));
  users.forEach((u: any) => {
    const k = monthKey(new Date(u.createdAt));
    if (k in data) data[k]++;
  });
  return keys.map(k => ({ month: k, users: data[k] }));
}

function filterByMonthYear(bookings: any[], year: number, month: number) {
  return bookings.filter((b: any) => {
    const d = new Date(b.createdAt);
    if (d.getFullYear() !== year) return false;
    if (month === 0) return true;
    return d.getMonth() + 1 === month;
  });
}

// ─────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  iconBg: string;
  growth?: number;
  subtitle?: React.ReactNode;
  valueColor?: string;
}> = ({ title, value, icon, iconBg, growth, subtitle, valueColor }) => (
  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
    <CardContent sx={{ p: 3 }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box flex={1}>
          <Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={valueColor ?? 'text.primary'}>
            {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
          </Typography>
          {growth !== undefined && (
            <Box display="flex" alignItems="center" mt={0.5} gap={0.5}>
              {growth >= 0
                ? <TrendingUp sx={{ fontSize: 14, color: colors.success }} />
                : <TrendingDown sx={{ fontSize: 14, color: colors.error }} />}
              <Typography variant="caption" color={growth >= 0 ? colors.success : colors.error} fontWeight={600}>
                {Math.abs(growth)}% vs last month
              </Typography>
            </Box>
          )}
          {subtitle && <Box mt={0.5}>{subtitle}</Box>}
        </Box>
        <Avatar sx={{ bgcolor: iconBg, width: 52, height: 52, borderRadius: 2 }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

// Total Revenue card, super admin + single-branch studio owner: same StatCard
// shell as every other card on this dashboard (for visual consistency in the
// grid). Hover shows a deliberately minimal breakdown — just Razorpay, Cash
// (only when there is any), and one grand total — not the Transactions page's
// full per-studio/per-gateway popups, which are too tall/detailed for this
// card's purpose.
const RevenueStatCardWithHoverBreakdown: React.FC<{
  title: string;
  stats: TransactionStats | null;
  isLoading: boolean;
  onSegmentClick: (sel: SegmentSelection) => void;
  // Super admin's card is global (no studioId) — its Razorpay/Cash counts
  // click into the allRazorpay/allCash buckets (every payment in the
  // platform-wide total, matching the count). A studio owner's card is
  // pinned to one studio, and the backend's studio-scoped drill-down route
  // rejects those global buckets ("Studios can only view their own gateway
  // and cash segments") — it only allows studioRazorpayAll/studioCashAll,
  // the merged-row equivalents of its own ownGateway+sweatSocialGateway /
  // ownAppCash+ssRoutedCash buckets. Default false (super admin usage).
  isStudioScoped?: boolean;
}> = ({ title, stats, isLoading, onSegmentClick, isStudioScoped }) => {
  // "Total Revenue" here means the studio's/platform's actual take: Razorpay
  // PLUS Cash/Manual, both net of whatever GST was actually recorded on them
  // — unlike the Transactions page, which deliberately keeps these as two
  // separate cards. Both are already independently filtered to exclude
  // cancelled bookings, refunded bookings, free/comped entries and pack
  // redemptions (`modeOfPayment: subscription`) — see getTransactionStats'
  // _isCancelledBooking/_isRefunded/_isFreeEntry and the SUBSCRIPTION/FREE
  // exclusion in its very first $match.
  // Derived by the shared helper so the brand dashboard cannot drift from
  // this one — see utils/revenue.ts.
  const { razorpayGross, cashGross, netRevenue, tax } = deriveRevenue(stats);
  // grandTotal.count mixes in cash counts (a pre-existing convention on this
  // endpoint — see its own "count includes cash too" comments), so the pure
  // Razorpay count is what's left after subtracting the Cash/Manual card's
  // own (already-accurate) count.
  const { razorpayCount, cashCount } = deriveRevenue(stats);
  const openRazorpaySegment = () =>
    onSegmentClick({
      studioName: 'Razorpay',
      // Super admin: every Razorpay payment in the platform total, not just
      // the Sweat Social (direct) slice — so the list matches the count.
      bucket: isStudioScoped ? 'studioRazorpayAll' : 'allRazorpay',
      bucketLabel: 'Razorpay',
      count: razorpayCount,
    });
  const openCashSegment = () =>
    onSegmentClick({
      studioName: 'Manual',
      bucket: isStudioScoped ? 'studioCashAll' : 'allCash',
      bucketLabel: 'Manual',
      count: cashCount,
    });
  return (
    <HoverPopover
      anchor={
        <StatCard
          title={title}
          value={`₹${netRevenue.toLocaleString('en-IN')}`}
          icon={<Payment sx={{ color: '#fff', fontSize: 26 }} />}
          iconBg={colors.warning}
          subtitle={
            <Box display="flex" flexDirection="column" gap={0.25}>
              {tax > 0 && (
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  + Tax {formatINR(tax)} = {formatINR(netRevenue + tax)} total
                </Typography>
              )}
              <Typography variant="caption" color={colors.primary} fontWeight={500}>
                Hover for breakdown
              </Typography>
            </Box>
          }
        />
      }
    >
      {isLoading && !stats ? (
        <Box sx={{ width: 240, p: 2.5, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box sx={{ width: 240 }}>
          <Box sx={{ px: 2, py: 1.25, bgcolor: `${colors.primary}0D`, borderBottom: `1px solid ${colors.divider}` }}>
            <Typography variant="subtitle2" fontWeight={700} color={colors.textPrimary}>
              Revenue breakdown
            </Typography>
          </Box>

          <Box sx={{ px: 2, py: 0.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" py={1} sx={cashGross > 0 ? { borderBottom: `1px solid ${colors.divider}` } : undefined}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.info, flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>Razorpay</Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="body2" fontWeight={700} color={colors.textPrimary}>{formatINR(razorpayGross)}</Typography>
                <Typography variant="caption" color={colors.primary} fontWeight={600}>
                  <TxnCountText count={razorpayCount} separator="" onClick={razorpayCount > 0 ? openRazorpaySegment : undefined} />
                </Typography>
              </Box>
            </Box>
            {cashGross > 0 && (
              <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.success, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>Manual</Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" fontWeight={700} color={colors.textPrimary}>{formatINR(cashGross)}</Typography>
                  <Typography variant="caption" color={colors.primary} fontWeight={600}>
                    <TxnCountText count={cashCount} separator="" onClick={cashCount > 0 ? openCashSegment : undefined} />
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: colors.backgroundTertiary, borderTop: `1px solid ${colors.divider}` }}>
            <Typography variant="body2" fontWeight={700} color={colors.textPrimary}>Total</Typography>
            <Typography variant="h6" fontWeight={800} color={colors.primary}>{formatINR(razorpayGross + cashGross)}</Typography>
          </Box>
        </Box>
      )}
    </HoverPopover>
  );
};

// Expiring Soon card (studio owner): active packs that end in the next 7 days
// or have 2 or fewer sessions left (backend status=expiring).
// Hover shows who — same HoverPopover as the Revenue card's breakdown, so it
// closes on mouse-leave or click-away and never leaves the dashboard.
function daysUntilLabel(date?: string) {
  if (!date) return '';
  const days = Math.ceil((new Date(date).getTime() - Date.now()) / 864e5);
  if (days <= 0) return 'today';
  return days === 1 ? 'in 1 day' : `in ${days} days`;
}

const ExpiringPacksStatCard: React.FC<{ packs: any[] | null }> = ({ packs }) => {
  const navigate = useNavigate();
  return (
    <HoverPopover
      placement="left"
      anchor={
        <StatCard
          title="Expiring Soon"
          value={packs?.length ?? '—'}
          valueColor={colors.error}
          icon={<HourglassBottom sx={{ color: '#fff', fontSize: 26 }} />}
          iconBg={colors.error}
          subtitle={
            <Typography variant="caption" color="text.secondary">
              Ending within 7 days or 2 sessions left
            </Typography>
          }
        />
      }
    >
      {packs === null ? (
        <Box sx={{ width: 420, p: 2.5, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box sx={{ width: 420 }}>
          <Box
            sx={{ px: 2.5, py: 1.5, bgcolor: `${colors.primary}0D`, borderBottom: `1px solid ${colors.divider}` }}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
          >
            <Typography variant="subtitle1" fontWeight={700} color={colors.textPrimary}>
              Ending in 7 days or almost used up
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/pack-subscriptions?status=expiring')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                borderColor: `${colors.primary}66`,
                color: colors.primary,
                flexShrink: 0,
                '&:hover': { borderColor: colors.primary, bgcolor: `${colors.primary}0f` },
              }}
            >
              View in Subscriptions
            </Button>
          </Box>

          {packs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              No packs ending this week or running low
            </Typography>
          ) : (
            <Box sx={{ px: 2.5, py: 0.5, maxHeight: 480, overflowY: 'auto' }}>
              {packs.map((p: any, i: number) => {
                const expiry = p.expiryDate || p.packEndDate;
                // Listed because the end date is close, or because it is almost
                // used up — show whichever reason applies.
                const endsSoon =
                  !!expiry && new Date(expiry).getTime() - Date.now() <= 7 * 864e5;
                const left = packSessionsLeft(p);
                return (
                  <Box
                    key={p._id}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    py={1.25}
                    sx={i < packs.length - 1 ? { borderBottom: `1px solid ${colors.divider}` } : undefined}
                  >
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={600} color={colors.textPrimary} noWrap sx={{ fontSize: 15 }}>
                        {packUserName(p)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 13 }}>
                        {packDisplayName(p)} · {packClassesLabel(p)} left
                      </Typography>
                    </Box>
                    <Box textAlign="right" flexShrink={0}>
                      <Typography variant="body2" fontWeight={700} color={colors.error} sx={{ fontSize: 15 }}>
                        {endsSoon || left === null
                          ? daysUntilLabel(expiry)
                          : `${left} session${left === 1 ? '' : 's'} left`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 13 }}>
                        {expiry ? new Date(expiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </HoverPopover>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" fontWeight={700} mb={2} color="text.primary">
    {children}
  </Typography>
);

// ─────────────────────────────────────────
// Booking status pill
// ─────────────────────────────────────────
const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <Chip label={status} size="small" color={statusColor(status) as any} sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
);

// ─────────────────────────────────────────
// Month/Year filter bar
// ─────────────────────────────────────────
const PeriodFilter: React.FC<{
  year: number; month: number;
  onYear: (y: number) => void; onMonth: (m: number) => void;
}> = ({ year, month, onYear, onMonth }) => (
  <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
    <FormControl size="small" sx={{ minWidth: 100 }}>
      <InputLabel>Year</InputLabel>
      <Select value={year} label="Year" onChange={e => onYear(Number(e.target.value))}>
        {[0, 1, 2].map(off => {
          const y = new Date().getFullYear() - off;
          return <MenuItem key={y} value={y}>{y}</MenuItem>;
        })}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>Month</InputLabel>
      <Select value={month} label="Month" onChange={e => onMonth(Number(e.target.value))}>
        {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
      </Select>
    </FormControl>
  </Box>
);

// ─────────────────────────────────────────
// Bookings mini table
// ─────────────────────────────────────────
const BookingsTableSkeleton: React.FC<{ showRevenue?: boolean }> = ({ showRevenue = true }) => (
  <TableContainer component={Paper} elevation={0}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
          <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Class / Session</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
          {showRevenue && <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>}
          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell>
              <Box display="flex" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={28} height={28} animation="wave" />
                <Skeleton variant="text" width={100} height={16} animation="wave" />
              </Box>
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={120} height={16} animation="wave" />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={80} height={16} animation="wave" />
            </TableCell>
            {showRevenue && (
              <TableCell align="right">
                <Skeleton variant="text" width={50} height={16} animation="wave" sx={{ ml: 'auto' }} />
              </TableCell>
            )}
            <TableCell>
              <Skeleton variant="rounded" width={72} height={24} animation="wave" sx={{ borderRadius: 4 }} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const BookingsTable: React.FC<{
  bookings: any[];
  showRevenue?: boolean;
  title?: string;
  loading?: boolean;
}> = ({ bookings, showRevenue = true, title = 'Recent Bookings', loading = false }) => {
  const navigate = useNavigate();
  const rows = bookings.slice(0, 10);
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            {title}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/bookings')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: `${colors.primary}66`,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                bgcolor: `${colors.primary}0f`,
              },
            }}
          >
            View All
          </Button>
        </Box>
        {loading ? (
          <BookingsTableSkeleton showRevenue={showRevenue} />
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" variant="body2">No bookings found</Typography>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Class / Session</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  {showRevenue && <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((b: any) => {
                  const rev = b.transaction
                    ? (b.transaction.basePrice || 0) + (b.transaction.taxes || 0)
                    : b.amount || 0;
                  const userName = b.user?.name || b.user?.email || '—';
                  const sessionName = b.session?.className || b.event?.eventName || '—';
                  const dateStr = b.createdAt ? new Date(b.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                  return (
                    <TableRow key={b._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: colors.primary }}>
                            {userName[0]?.toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>{userName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>{sessionName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{dateStr}</Typography>
                      </TableCell>
                      {showRevenue && (
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color={colors.success}>
                            ₹{rev.toLocaleString('en-IN')}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell><StatusChip status={b.bookingStatus || b.status || 'Unknown'} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────
// Active packs section (studio owner)
// ─────────────────────────────────────────
const PACK_BAR_COLORS = ['#f48fb4', '#e96d9a', '#7B1FA2', '#0288D1', '#4caf50', '#ff9800', '#5c6bc0', '#26a69a'];

function packDisplayName(p: any) {
  return p.title || p.pack?.title || 'Unknown Pack';
}

function packUserName(p: any) {
  return p.user?.name || p.user?.email || 'Unknown';
}

// Sessions still to come: credits are deducted at booking, so upcoming booked
// sessions count as left — same as the Subscriptions list's "6 left (6 upcoming)".
function packSessionsLeft(p: any): number | null {
  if (typeof p.classesLeft !== 'number') return null;
  return p.classesLeft + (p.pendingBookingsCount ?? 0);
}

function packClassesLabel(p: any) {
  if (!p.totalClasses) return 'Unlimited';
  return `${packSessionsLeft(p) ?? 0}/${p.totalClasses}`;
}


function calcActivePacksInsights(packs: any[]) {
  const byType = new Map<string, { subscribers: number; members: { name: string; classes: string }[]; revenue: number }>();

  const uniqueUsers = new Set<string>();
  let totalRevenue = 0;

  packs.forEach((p: any) => {
    const name = packDisplayName(p);
    const userName = packUserName(p);
    uniqueUsers.add(p.user?._id || userName);
    // The pack's own `totalPrice` is the catalogue face value, copied onto
    // every purchase regardless of how it was paid for — a free/comped
    // assignment still carries it even though ₹0 changed hands. `amountPaid`
    // (from the linked transaction's basePrice + taxes, backend-computed) is
    // the real amount collected, so a free pack correctly contributes 0.
    const price = p.amountPaid ?? p.totalPrice ?? p.pack?.totalPrice ?? 0;
    totalRevenue += price;
    const g = byType.get(name) || { subscribers: 0, members: [], revenue: 0 };
    g.subscribers += 1;
    g.members.push({ name: userName, classes: packClassesLabel(p) });
    g.revenue += price;
    byType.set(name, g);
  });

  return {
    total: packs.length,
    uniqueMembers: uniqueUsers.size,
    totalRevenue: Math.round(totalRevenue),
    byType: Array.from(byType.entries())
      .map(([name, g]) => ({ name, subscribers: g.subscribers, members: g.members, revenue: g.revenue }))
      .sort((a, b) => b.subscribers - a.subscribers || a.name.localeCompare(b.name)),
  };
}

const ActivePacksTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, subscribers, revenue, members } = payload[0].payload;
  return (
    <Box sx={{ bgcolor: 'white', border: '1px solid #eee', borderRadius: 2, p: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', minWidth: 200, maxWidth: 260, maxHeight: 300 }}>
      <Typography variant="body2" fontWeight={700} mb={0.25}>{name}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={members?.length ? 1 : 0}>
        {subscribers} subscriber{subscribers !== 1 ? 's' : ''}
        {revenue > 0 && ` · ₹${revenue.toLocaleString('en-IN')}`}
      </Typography>
      {members?.length > 0 ? (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1, maxHeight: 220, overflowY: 'auto' }}>
          {members.map((m: { name: string; classes: string }, i: number) => (
            <Box key={i} display="flex" justifyContent="space-between" alignItems="center" gap={2} py={0.4}>
              <Typography variant="caption" color="text.primary" noWrap sx={{ flex: 1 }}>
                {m.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" flexShrink={0}>
                {m.classes}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.disabled">No purchases this period</Typography>
      )}
    </Box>
  );
};

const ActivePacksSkeleton: React.FC = () => (
  <Skeleton variant="rounded" height={280} animation="wave" sx={{ borderRadius: 2 }} />
);

const ActivePacksSection: React.FC<{ packs: any[]; periodLabel: string; loading?: boolean }> = ({
  packs, periodLabel, loading = false,
}) => {
  const navigate = useNavigate();
  const insights = useMemo(() => calcActivePacksInsights(packs), [packs]);
  const chartHeight = Math.max(280, insights.byType.length * 36 + 80);
  const chartCaption = [
    `${insights.total} active`,
    `${insights.uniqueMembers} member${insights.uniqueMembers !== 1 ? 's' : ''}`,
    insights.totalRevenue > 0 ? `₹${insights.totalRevenue.toLocaleString('en-IN')} revenue` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Active Packs — {periodLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Hover a bar to see who purchased each pack this period
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/pack-subscriptions')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: `${colors.primary}66`,
              color: colors.primary,
              flexShrink: 0,
              '&:hover': {
                borderColor: colors.primary,
                bgcolor: `${colors.primary}0f`,
              },
            }}
          >
            View All
          </Button>
        </Box>

        {loading ? (
          <ActivePacksSkeleton />
        ) : insights.byType.length === 0 ? (
          <Box py={4} textAlign="center">
            <ShoppingBag sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">
              No pack types configured for this studio
            </Typography>
          </Box>
        ) : (
          <>
            {chartCaption && (
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                {chartCaption}
              </Typography>
            )}
            <Box height={chartHeight}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={insights.byType} margin={{ top: 20, right: 8, left: 0, bottom: insights.byType.length > 4 ? 72 : 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={insights.byType.length > 4 ? -32 : insights.byType.length > 2 ? -18 : 0}
                    textAnchor={insights.byType.length > 2 ? 'end' : 'middle'}
                    height={insights.byType.length > 4 ? 80 : insights.byType.length > 2 ? 56 : 40}
                    tickFormatter={(v: string) => (v.length > 18 ? `${v.slice(0, 16)}…` : v)}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                    width={36}
                    label={{ value: 'Subscribers', angle: -90, position: 'insideLeft', offset: 12, style: { fontSize: 11, fill: '#666' } }}
                  />
                  <ChartTooltip content={<ActivePacksTooltip />} cursor={{ fill: `${colors.primary}0a` }} />
                  <Bar dataKey="subscribers" name="Subscribers" radius={[6, 6, 0, 0]} maxBarSize={56}>
                    <LabelList dataKey="subscribers" position="top" style={{ fontSize: 11, fontWeight: 600, fill: colors.textSecondary }} />
                    {insights.byType.map((entry, i) => (
                      <Cell
                        key={entry.name}
                        fill={entry.subscribers > 0 ? PACK_BAR_COLORS[i % PACK_BAR_COLORS.length] : `${colors.divider}`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────
// Active packs by studio (super admin) — pie chart + ranked list
// ─────────────────────────────────────────
const PASTEL_STUDIO_COLORS = [
  '#F4A6C1', '#9FA8DA', '#81C7B0', '#F5B87F', '#C5D97A',
  '#B69AD9', '#F08A8A', '#7FC8CB', '#E3C878', '#93CF7E',
];

function packStudioName(p: any) {
  return p.studio?.studioName || 'SweatSocial (Global)';
}

function calcPacksByStudio(packs: any[]) {
  const byStudio = new Map<string, { count: number; revenue: number }>();
  let totalRevenue = 0;

  packs.forEach((p: any) => {
    const name = packStudioName(p);
    const price = p.totalPrice || p.pack?.totalPrice || 0;
    totalRevenue += price;
    const g = byStudio.get(name) || { count: 0, revenue: 0 };
    g.count += 1;
    g.revenue += price;
    byStudio.set(name, g);
  });

  return {
    total: packs.length,
    totalRevenue: Math.round(totalRevenue),
    studios: Array.from(byStudio.entries())
      .map(([name, g]) => ({ name, count: g.count, revenue: Math.round(g.revenue) }))
      .sort((a, b) => b.count - a.count),
  };
}

const PacksByStudioTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, count, revenue } = payload[0].payload;
  return (
    <Box sx={{ bgcolor: 'white', border: '1px solid #eee', borderRadius: 2, p: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
      <Typography variant="body2" fontWeight={700} mb={0.25}>{name}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {count} active pack{count !== 1 ? 's' : ''}
        {revenue > 0 && ` · ₹${revenue.toLocaleString('en-IN')}`}
      </Typography>
    </Box>
  );
};

const PacksByStudioSkeleton: React.FC = () => (
  <Skeleton variant="rounded" height={300} animation="wave" sx={{ borderRadius: 2 }} />
);

const PacksByStudioSection: React.FC<{ packs: any[]; periodLabel: string; loading?: boolean }> = ({
  packs, periodLabel, loading = false,
}) => {
  const navigate = useNavigate();
  const insights = useMemo(() => calcPacksByStudio(packs), [packs]);

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Active Packs by Studio — {periodLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {insights.total} active pack{insights.total !== 1 ? 's' : ''} across {insights.studios.length} studio{insights.studios.length !== 1 ? 's' : ''}
              {insights.totalRevenue > 0 && ` · ₹${insights.totalRevenue.toLocaleString('en-IN')} revenue`}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/pack-subscriptions')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: `${colors.primary}66`,
              color: colors.primary,
              flexShrink: 0,
              '&:hover': {
                borderColor: colors.primary,
                bgcolor: `${colors.primary}0f`,
              },
            }}
          >
            View All
          </Button>
        </Box>

        {loading ? (
          <PacksByStudioSkeleton />
        ) : insights.studios.length === 0 ? (
          <Box py={4} textAlign="center">
            <ShoppingBag sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">
              No active packs for this period
            </Typography>
          </Box>
        ) : (
          <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
            <Box flex="1 1 280px" height={280} minWidth={240}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={insights.studios}
                    dataKey="count"
                    nameKey="name"
                    innerRadius="52%"
                    outerRadius="88%"
                    paddingAngle={2}
                    cornerRadius={4}
                    stroke="none"
                  >
                    {insights.studios.map((entry, i) => (
                      <Cell key={entry.name} fill={PASTEL_STUDIO_COLORS[i % PASTEL_STUDIO_COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<PacksByStudioTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <Box flex="1 1 260px" minWidth={240} maxHeight={280} sx={{ overflowY: 'auto', pr: 0.5 }}>
              {insights.studios.map((s, i) => (
                <Box
                  key={s.name}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1.5}
                  py={0.9}
                  sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}
                >
                  <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: PASTEL_STUDIO_COLORS[i % PASTEL_STUDIO_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" noWrap>{s.name}</Typography>
                  </Box>
                  <Box display="flex" alignItems="baseline" gap={1} flexShrink={0}>
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                      {s.count}
                    </Typography>
                    {s.revenue > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        ₹{s.revenue.toLocaleString('en-IN')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};


// ─────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────
const Dashboard: React.FC = () => {
  const { admin } = useAuth();

  const role = admin?.role || '';
  const isSuperAdmin = role === 'super_admin' || role === 'admin';
  const isStudioOwner = isOwnerRole(role);
  /**
   * Does this person run more than one location?
   *
   * Was `admin.studio.isBranchParent`, a flag on the studio the token named.
   * Two problems: the flag was stale on real studios (two in test were marked
   * as branch parents with zero branches), and a token belonging to a MANAGER
   * carries the studio's id, so a floor manager at a multi-branch business
   * matched it and was shown the whole organisation's revenue.
   *
   * The server now answers it: `/brands/my` returns the branches this PERSON
   * holds, so more than one means an organisation view, and a manager at one
   * branch gets the ordinary dashboard.
   */
  const [myBranchCount, setMyBranchCount] = useState<number | null>(null);
  const [myBrandName, setMyBrandName] = useState<string | null>(null);

  /**
   * ── TWO STATES, AND THE SWITCHER PICKS BETWEEN THEM ─────────────────────
   *
   * "All locations" (the default for anyone holding more than one) shows the
   * brand-wide report: combined revenue and bookings, broken down per
   * location.
   *
   * Choosing ONE location drops straight back to the ordinary studio-owner
   * dashboard — deliberately the same screen a single-studio owner sees,
   * unchanged. A brand owner looking at one branch wants that branch's
   * dashboard, not a one-row version of the org report.
   *
   * `isHQ` used to be true whenever the person held more than one location,
   * so the org view was the ONLY view they could ever get.
   */
  const selectedLocation = getSelectedLocation(admin?.id);
  const isAllLocations = selectedLocation === ALL_LOCATIONS;
  const isHQ = isStudioOwner && (myBranchCount ?? 0) > 1 && isAllLocations;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Shared "Total Revenue" card state — super admin and single-branch studio
  // owner both source this from the same getTransactionStats aggregate the
  // Transactions tab uses (see fetchDashboardRevenue), so the number on this
  // dashboard always matches that role's own Transactions tab exactly.
  const [dashboardStats, setDashboardStats] = useState<TransactionStats | null>(null);
  const [dashboardRange, setDashboardRange] = useState<{ startDate: string; endDate: string } | null>(null);
  // Drives the drill-down dialog when a "txns" count on the revenue card's
  // hover breakdown is clicked.
  const [dashboardSegmentSel, setDashboardSegmentSel] = useState<SegmentSelection | null>(null);

  // Super admin state
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalStudios, setTotalStudios] = useState(0);
  const [userGrowth, setUserGrowth] = useState(0);
  const [userGrowthData, setUserGrowthData] = useState<any[]>([]);
  // Non-cancelled Sweat Social bookings in the selected period (null until loaded).
  const [saBookingsCount, setSaBookingsCount] = useState<number | null>(null);
  const [saMonthlyBookings, setSaMonthlyBookings] = useState<any[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [saPacks, setSaPacks] = useState<any[]>([]);
  const [saPacksLoading, setSaPacksLoading] = useState(true);

  // Studio owner / manager state
  const [bookings, setBookings] = useState<any[]>([]);
  // Feeds the "Monthly Revenue" chart, currently commented out below in
  // favor of the Active Packs table — left wired up so re-enabling the
  // chart is a pure uncomment.
  // const [monthlyRevenue, setMonthlyRevenue] = useState<any[]>([]);
  const [monthlyBookings, setMonthlyBookings] = useState<any[]>([]);
  const [activeClassCount, setActiveClassCount] = useState(0);
  const [liveEventCount, setLiveEventCount] = useState(0);
  const [activePacks, setActivePacks] = useState<any[]>([]);
  const [packsLoading, setPacksLoading] = useState(true);
  // Packs expiring within 7 days (null until loaded). Not tied to the period
  // filter — "expiring soon" is always measured from today.
  const [expiringPacks, setExpiringPacks] = useState<any[] | null>(null);

  // The org view's own state, fetch and period filter are gone with it —
  // BrandDashboard owns all three, and owns them next to the numbers they
  // describe.

  // Shared period filter (studio/manager)
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  // Derived — reacts immediately when year/month/bookings changes
  const filteredBookings = useMemo(
    () => filterByMonthYear(bookings, year, month),
    [bookings, year, month]
  );

  // ── Fetch functions ──────────────────────────────
  // Users, studios, and the all-time monthly trend chart — loaded once on
  // mount. The trend chart is now backed by a dedicated $group aggregation
  // (getBookingsTrend) instead of paging through up to 2000 full booking
  // documents and bucketing them client-side.
  const fetchSuperAdminData = async () => {
    try {
      setIsLoading(true);
      const [usersRes, studiosRes, trendRes] = await Promise.all([
        apiService.getUsers(),
        apiService.getStudios(),
        apiService.getBookingsTrend(),
      ]);
      const allUsers = usersRes.data || usersRes.users || [];
      const studios = studiosRes.data || studiosRes.studios || [];

      // Restrict dashboard to Sweat Social only — exclude whitelabelled app data
      const users = allUsers.filter(isSweatSocialUser);

      const now = new Date();
      const thisMonth = users.filter((u: any) => {
        const d = new Date(u.createdAt);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length;
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1);
      const lastMonthCount = users.filter((u: any) => {
        const d = new Date(u.createdAt);
        return d.getMonth() === lastMonthDate.getMonth() && d.getFullYear() === lastMonthDate.getFullYear();
      }).length;
      const growth = lastMonthCount > 0 ? Math.round(((thisMonth - lastMonthCount) / lastMonthCount) * 100) : 0;

      setTotalUsers(users.length);
      setTotalStudios(studios.length);
      setUserGrowth(growth);
      setUserGrowthData(calcMonthlyUsers(users));
      setSaMonthlyBookings(
        (trendRes.months || []).map((m: any) => {
          const [yy, mm] = String(m.label).split('-');
          return { month: `${MONTH_NAMES[Number(mm) - 1]} ${yy}`, bookings: m.count };
        }),
      );
    } catch (e: any) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  // Bookings stat card — a server-side count for the selected IST month (or
  // whole year when month = "All Year"). Used to pull up to 2000 fully-joined
  // bookings via getAllBookings just to count them, which timed out (30s) and
  // then blanked the whole dashboard. A failure here is non-fatal: the card
  // shows "—" and the rest of the page stays.
  const fetchSuperAdminBookings = useCallback(async (y: number, m: number) => {
    try {
      setBookingsLoading(true);
      const res = await apiService.getSweatSocialBookingsCount(istRevenueRangeISO(y, m));
      setSaBookingsCount(typeof res?.count === 'number' ? res.count : null);
    } catch {
      setSaBookingsCount(null);
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  // Active-packs-by-studio chart — mirrors fetchActivePacks below but unscoped
  // (super admin's getPurchasedPacks call returns packs across every studio,
  // each carrying its own `studio` ref), refetched whenever the period changes.
  const fetchSuperAdminPacks = useCallback(async (y: number, m: number) => {
    try {
      setSaPacksLoading(true);
      const res = await apiService.getPurchasedPacks({
        status: 'active',
        ...revenueRangeISO(y, m),
        pageSize: 2000,
      });
      setSaPacks(res.purchasedPacks || res.data || []);
    } catch {
      /* keep previous values on transient failure */
    } finally {
      setSaPacksLoading(false);
    }
  }, []);

  // Resolved once per session; the answer only changes when a branch is added.
  useEffect(() => {
    if (!isStudioOwner) return;
    let cancelled = false;
    apiService
      .getMyBrand()
      .then((res: any) => {
        if (!cancelled) {
          setMyBranchCount(res?.studios?.length ?? 0);
          setMyBrandName(res?.brand?.name ?? null);
        }
      })
      .catch(() => {
        // A studio with no brand row yet is simply not an organisation.
        if (!cancelled) setMyBranchCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [isStudioOwner]);

  // "Total Revenue" card (selected period only) sourced from the exact same
  // backend aggregate as the Transactions tab (getTransactionStats) — for
  // either role, grandTotal.netRevenue there is "Razorpay revenue + what
  // came from us" (own gateway + Sweat Social gateway, net of GST), scoped
  // automatically by apiService per role (studio owner → that one studio's
  // STATS_STUDIO endpoint; super admin → the unscoped, all-studios STATS
  // endpoint) — so this card always matches that role's own Transactions tab
  // number for the same period. Used for super admin and single-branch
  // studio owner only; HQ multi-branch has its own org-overview dashboard.
  const fetchDashboardRevenue = useCallback(async (y: number, m: number) => {
    try {
      const range = istRevenueRangeISO(y, m);
      const periodRes = await apiService.getTransactionStats(range);
      setDashboardStats(periodRes);
      setDashboardRange(range);
    } catch {
      /* keep previous value on transient failure */
    }
  }, []);

  // Monthly revenue chart (last 6 months) — correct series incl. pack purchases.
  // Commented out along with the chart and its state above; re-enable together.
  // const fetchStudioMonthlyRevenue = useCallback(async () => {
  //   const sid = admin?.studio?._id || '';
  //   if (!sid) return;
  //   try {
  //     const res = await apiService.getEarningsSummary({ type: 'monthly' });
  //     const series = res?.earningsSummary || [];
  //     setMonthlyRevenue(
  //       series.map((s: any) => {
  //         const [yy, mm] = String(s.label).split('-');
  //         const label = `${MONTH_NAMES[Number(mm) - 1] || mm} ${yy}`;
  //         return { month: label, revenue: Math.round(s.earnings || 0) };
  //       }),
  //     );
  //   } catch {
  //     /* keep previous values on transient failure */
  //   }
  // }, [admin]);

  // Active Packs chart — active packs purchased in the selected year/month
  // (or whole year when month = "All Year"), refetched whenever the period
  // filter changes, same as fetchDashboardRevenue above. Reuses the existing
  // packs list endpoint's status=active + startDate/endDate filters — no new
  // endpoint. pageSize is large enough to cover one studio's full active-pack
  // list for a period, so the by-pack-type breakdown counts everything.
  const fetchActivePacks = useCallback(async (y: number, m: number) => {
    try {
      setPacksLoading(true);
      const res = await apiService.getPurchasedPacks({
        status: 'active',
        ...revenueRangeISO(y, m),
        pageSize: 500,
      });
      setActivePacks(res.purchasedPacks || res.data || []);
    } catch {
      /* keep previous values on transient failure */
    } finally {
      setPacksLoading(false);
    }
  }, []);

  // Expiring Soon card — the count and the hover list are the same array, so
  // they can't disagree. Backend returns soonest expiry first; pageSize covers
  // a week's worth of expiries for any one studio.
  const fetchExpiringPacks = useCallback(async () => {
    try {
      const res = await apiService.getPurchasedPacks({ status: 'expiring', pageSize: 500 });
      setExpiringPacks(res.purchasedPacks || res.data || []);
    } catch {
      /* keep previous value on transient failure */
    }
  }, []);

  // Single-branch studio owners: bookings scoped to the selected year/month
  // (or the whole year when month = "All Year"), refetched on period change —
  // same fix as the super admin dashboard's pageSize:2000 issue. This used to
  // pull up to 1000 bookings regardless of date and filter client-side; now
  // the date range is applied server-side, so it only ever fetches the one
  // period actually being viewed. (monthlyBookings is no longer computed here
  // since the chart that used it is commented out above; fetchManagerData
  // still computes its own copy for the Manager Dashboard's live chart.)
  const fetchStudioBookings = useCallback(async (y: number, m: number) => {
    try {
      setBookingsLoading(true);
      const res = await apiService.getBookings({ ...revenueRangeISO(y, m), pageSize: 1000 });
      setBookings(res.bookings || res.data || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  const fetchManagerData = async () => {
    setIsLoading(false);
    try {
      setBookingsLoading(true);
      const [bkRes, clRes, evRes] = await Promise.all([
        apiService.getBookings({ pageSize: 1000 }),
        apiService.getAllClasses({}),
        apiService.getEvents({}),
      ]);

      const bks = bkRes.bookings || bkRes.data || [];
      setBookings(bks);
      setMonthlyBookings(calcMonthlyBookings(bks));

      const classes = clRes.classes || clRes.data || [];
      setActiveClassCount(classes.filter((c: any) => c.isActive !== false).length);

      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const events = evRes.events || evRes.data || [];
      const liveEvents = events.filter((e: any) => {
        const eventDate = new Date(e.eventDate || e.date || e.startDate || 0);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= now;
      });
      setLiveEventCount(liveEvents.length);
    } catch (e: any) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setBookingsLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) fetchSuperAdminData();
    else if (!isStudioOwner) fetchManagerData();
    // Single-branch studio owners have nothing period-independent to fetch on
    // mount — bookings/revenue/packs are all period-scoped, handled by the
    // effects below (which also fire on mount, same as this one).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, isHQ, isStudioOwner]);

  // "Total Revenue" card: super admin and single-branch studio owners only
  // (HQ multi-branch keeps its own org-overview numbers) — refetched whenever
  // the selected period changes (runs on mount too).
  useEffect(() => {
    if (isSuperAdmin || (isStudioOwner && !isHQ)) fetchDashboardRevenue(year, month);
  }, [year, month, isSuperAdmin, isStudioOwner, isHQ, fetchDashboardRevenue]);

  // Single-branch studio owners: refresh bookings whenever the selected
  // period changes (runs on mount too).
  useEffect(() => {
    if (isStudioOwner && !isHQ) fetchStudioBookings(year, month);
  }, [year, month, isStudioOwner, isHQ, fetchStudioBookings]);

  // Single-branch studio owners: refresh the Active Packs chart whenever the
  // selected period changes (runs on mount too).
  useEffect(() => {
    if (isStudioOwner && !isHQ) fetchActivePacks(year, month);
  }, [year, month, isStudioOwner, isHQ, fetchActivePacks]);

  useEffect(() => {
    if (isStudioOwner && !isHQ) fetchExpiringPacks();
  }, [isStudioOwner, isHQ, fetchExpiringPacks]);

  // Super admin: refresh the stat cards (bookings count) whenever the
  // selected period changes (runs on mount too).
  useEffect(() => {
    if (isSuperAdmin) fetchSuperAdminBookings(year, month);
  }, [year, month, isSuperAdmin, fetchSuperAdminBookings]);

  // Super admin: refresh the Active Packs by Studio chart whenever the
  // selected period changes (runs on mount too).
  useEffect(() => {
    if (isSuperAdmin) fetchSuperAdminPacks(year, month);
  }, [year, month, isSuperAdmin, fetchSuperAdminPacks]);



  // ── Loading / Error ──────────────────────────────
  //
  // `isHQ` is deliberately NOT part of this. The brand view owns its own
  // loading state, and `isLoading` is only ever cleared by the super-admin and
  // manager fetches — so gating on it left a brand owner on a spinner that
  // nothing would ever turn off.
  const showPageLoader = isLoading && isSuperAdmin;

  // Until `/brands/my` answers we do not know whether this person holds one
  // location or six, and `isHQ` reads false in the meantime. Rendering on that
  // would show the single-studio dashboard for a moment and then replace it —
  // so wait for the answer instead of flashing the wrong screen.
  if (isStudioOwner && myBranchCount === null) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (showPageLoader) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // ─────────────────────────────────────────────────
  // SUPER ADMIN DASHBOARD
  // ─────────────────────────────────────────────────
  if (isSuperAdmin) {
    return (
      <Box>
        {/* Header */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>Platform Dashboard</Typography>
            <Typography variant="body2" color="text.secondary">
              Overview of all studios, users, and bookings on Sweat Social
            </Typography>
          </Box>
          <PeriodFilter year={year} month={month} onYear={setYear} onMonth={setMonth} />
        </Box>

        {/* Stat Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', lg: 'repeat(4,1fr)' }, gap: 2.5, mb: 4 }}>
          <StatCard
            title="Total Users (SS)"
            value={totalUsers}
            icon={<People sx={{ color: '#fff', fontSize: 26 }} />}
            iconBg={colors.primary}
            growth={userGrowth}
          />
          <StatCard
            title="Total Studios"
            value={totalStudios}
            icon={<Business sx={{ color: '#fff', fontSize: 26 }} />}
            iconBg="#0288D1"
          />
          <StatCard
            title={month === 0 ? 'Total Bookings' : `Bookings (${MONTHS[month].label})`}
            value={saBookingsCount ?? '—'}
            icon={<BookOnline sx={{ color: '#fff', fontSize: 26 }} />}
            iconBg={colors.success}
          />
          <RevenueStatCardWithHoverBreakdown
            title={month === 0 ? 'Total Revenue' : `Revenue (${MONTHS[month].label})`}
            stats={dashboardStats}
            isLoading={isLoading}
            onSegmentClick={setDashboardSegmentSel}
          />
        </Box>

        {/* Charts Row */}
        <Box sx={{ mb: 3 }}>
          {/* User Growth Line Chart */}
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <CardContent>
              <SectionTitle>User Registrations — Since Apr 2025</SectionTitle>
              <Box height={260}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={userGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <ChartTooltip />
                    <Line type="monotone" dataKey="users" stroke={colors.primary} strokeWidth={2.5} dot={{ r: 4, fill: colors.primary }} name="New Users" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Monthly Bookings Bar */}
        <Box mb={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <CardContent>
              <SectionTitle>Monthly Bookings — Since Apr 2025</SectionTitle>
              <Box height={260}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={saMonthlyBookings}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <ChartTooltip />
                    <Bar dataKey="bookings" fill={colors.primary} radius={[4, 4, 0, 0]} name="Bookings" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Active Packs by Studio */}
        <PacksByStudioSection
          packs={saPacks}
          periodLabel={month === 0 ? `${year}` : `${MONTHS[month].label} ${year}`}
          loading={saPacksLoading}
        />

        <SegmentTransactionsDialog
          open={Boolean(dashboardSegmentSel)}
          selection={dashboardSegmentSel}
          startDate={dashboardRange?.startDate}
          endDate={dashboardRange?.endDate}
          periodLabel={month === 0 ? `${year}` : `${MONTHS[month].label} ${year}`}
          onClose={() => setDashboardSegmentSel(null)}
        />
      </Box>
    );
  }

  // ─────────────────────────────────────────────────
  // BRAND OWNER — "All locations"
  // ─────────────────────────────────────────────────
  //
  // The old block here computed its own revenue from `orgOverview` and
  // disagreed with the very dashboards it summarised: it reported ₹1,87,251
  // for Koramangala while that studio's own dashboard reported ₹1,25,702 for
  // the same month. Two numbers, both shown as fact.
  //
  // BrandDashboard does not compute revenue at all — it calls the endpoint
  // each studio dashboard already calls, once per location, and adds the
  // results up. See utils/revenue.ts.
  if (isHQ) {
    return <BrandDashboard brandName={myBrandName} />;
  }


  // ─────────────────────────────────────────────────
  // STUDIO OWNER — single branch
  // ─────────────────────────────────────────────────
  if (isStudioOwner) {
    const studioName = admin?.studio?.studioName || admin?.name || 'Your Studio';

    const prevMonthIdx = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const prevMonthBookings = filterByMonthYear(bookings, prevYear, prevMonthIdx);
    const nonCancelledCount = filteredBookings.filter((b: any) => b.bookingStatus !== 'CANCELLED').length;
    const prevNonCancelledCount = prevMonthBookings.filter((b: any) => b.bookingStatus !== 'CANCELLED').length;
    const bookingGrowth = prevNonCancelledCount > 0
      ? Math.round(((nonCancelledCount - prevNonCancelledCount) / prevNonCancelledCount) * 100)
      : 0;

    return (
      <Box>
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>Studio Dashboard</Typography>
            <Typography variant="body2" color="text.secondary">
              {studioName} — your studio at a glance
            </Typography>
          </Box>
          <PeriodFilter year={year} month={month} onYear={setYear} onMonth={setMonth} />
        </Box>

        {/* Stat Cards — all sourced from data already fetched for the charts/
            table below (bookings, activePacks), no extra calls. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
          <StatCard
            title={month === 0 ? 'Total Bookings' : `Bookings (${MONTHS[month].label})`}
            value={nonCancelledCount}
            icon={<BookOnline sx={{ color: '#fff', fontSize: 26 }} />}
            iconBg={colors.success}
            growth={month > 0 ? bookingGrowth : undefined}
          />
          <RevenueStatCardWithHoverBreakdown
            title={month === 0 ? 'Total Revenue' : `Revenue (${MONTHS[month].label})`}
            stats={dashboardStats}
            isLoading={isLoading}
            onSegmentClick={setDashboardSegmentSel}
            isStudioScoped
          />
          <StatCard
            title={month === 0 ? 'Active Packs' : `Active Packs (${MONTHS[month].label})`}
            value={activePacks.length}
            icon={<ShoppingBag sx={{ color: '#fff', fontSize: 26 }} />}
            iconBg="#0288D1"
          />
          <ExpiringPacksStatCard packs={expiringPacks} />
        </Box>

        {/* Charts — replaced by the Active Packs table below (kept here,
            commented out, in case the trend charts need to come back). */}
        {/*
        <Box sx={{ mb: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <CardContent>
              <SectionTitle>Monthly Revenue — Last 6 Months</SectionTitle>
              <Box height={260}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <ChartTooltip formatter={(val: any) => [`₹${val.toLocaleString('en-IN')}`, 'Revenue']} />
                    <Bar dataKey="revenue" fill={colors.warning} radius={[4, 4, 0, 0]} name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box mb={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <CardContent>
              <SectionTitle>Monthly Bookings — Last 6 Months</SectionTitle>
              <Box height={240}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyBookings}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <ChartTooltip />
                    <Line type="monotone" dataKey="bookings" stroke={colors.success} strokeWidth={2.5} dot={{ r: 4 }} name="Bookings" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>
        */}

        {/* Active Packs */}
        <Box mb={3}>
          <ActivePacksSection
            packs={activePacks}
            periodLabel={month === 0 ? `${year}` : `${MONTHS[month].label} ${year}`}
            loading={packsLoading}
          />
        </Box>

        {/* Analytics — OWNER ONLY. Rendered inside the isStudioOwner branch,
            and the endpoint behind it is StudioAdminOnlyGuard, so a manager or
            instructor cannot reach the data even by calling it directly.
            It carries its own date range because these questions ("how did the
            last 90 days go") are asked over a different window than the
            month-scoped cards above. */}
        <StudioAnalytics />

        {/* Bookings Table */}
        <BookingsTable
          bookings={filteredBookings.length > 0 ? filteredBookings : [...bookings].reverse()}
          showRevenue
          loading={bookingsLoading}
          title={month === 0 ? 'Recent Bookings' : `Bookings — ${MONTHS[month].label} ${year}`}
        />

        <SegmentTransactionsDialog
          open={Boolean(dashboardSegmentSel)}
          selection={dashboardSegmentSel}
          startDate={dashboardRange?.startDate}
          endDate={dashboardRange?.endDate}
          periodLabel={month === 0 ? `${year}` : `${MONTHS[month].label} ${year}`}
          onClose={() => setDashboardSegmentSel(null)}
        />
      </Box>
    );
  }

  // ─────────────────────────────────────────────────
  // MANAGER DASHBOARD (no revenue)
  // ─────────────────────────────────────────────────
  const studioName = admin?.studio?.studioName || admin?.name || 'Studio';

  return (
    <Box>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            {studioName} — welcome back
          </Typography>
        </Box>
        <PeriodFilter year={year} month={month} onYear={setYear} onMonth={setMonth} />
      </Box>

      {/* Stat Cards — no revenue */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', lg: 'repeat(4,1fr)' }, gap: 2.5, mb: 4 }}>
        <StatCard
          title={month === 0 ? 'Total Bookings' : `Bookings (${MONTHS[month].label})`}
          value={filteredBookings.filter((b: any) => b.bookingStatus !== 'CANCELLED').length}
          icon={<BookOnline sx={{ color: '#fff', fontSize: 26 }} />}
          iconBg={colors.success}
        />
        <StatCard
          title="Active Classes"
          value={activeClassCount}
          icon={<Class sx={{ color: '#fff', fontSize: 26 }} />}
          iconBg={colors.primary}
        />
        <StatCard
          title="Live Events"
          value={liveEventCount}
          icon={<EventNote sx={{ color: '#fff', fontSize: 26 }} />}
          iconBg="#7B1FA2"
        />
      </Box>

      {/* Charts */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <CardContent>
            <SectionTitle>Monthly Bookings — Last 6 Months</SectionTitle>
            <Box height={260}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyBookings}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <ChartTooltip />
                  <Bar dataKey="bookings" fill={colors.primary} radius={[4, 4, 0, 0]} name="Bookings" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Bookings Table — no amount column */}
      <BookingsTable
        bookings={filteredBookings.length > 0 ? filteredBookings : [...bookings].reverse()}
        showRevenue={false}
        loading={bookingsLoading}
        title={month === 0 ? 'Recent Bookings' : `Bookings — ${MONTHS[month].label} ${year}`}
      />
    </Box>
  );
};

export default Dashboard;
